import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { InlineMath, BlockMath } from "react-katex";
import "katex/dist/katex.min.css";
import he from "he"; // Import the 'he' library to decode HTML entities
import { Modal } from "react-bootstrap";
import { FaFilter } from "react-icons/fa"; // for menu icon
import "./PickQuestions.css"


const PickQuestions = () => {
  const navigate = useNavigate()
  const { id } = useParams();
  const [questions, setQuestions] = useState([]);
  const [subjectLimits, setSubjectLimits] = useState({});
  const [fetchedData, setFetchedData] = useState([]);
  const [selectedChapter, setSelectedChapter] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedQuestionType, setSelectedQuestionType] = useState("");
  const [selectedExamType, setSelectedExamType] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [levels, setLevels] = useState([]);
  const [questionType, setQuestionType] = useState([]);
  const [examType, setExamType] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [examTypeFilter, setExamTypeFilter] = useState(""); // For Exam Type
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const [showFilter, setShowFilter] = useState(false);

  const [page, setPage] = useState(1);

  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pickedQuestions, setPickedQuestions] = useState([]);
  const [subjectMap, setSubjectMap] = useState({});

  const [loading, setLoading] = useState(false); // for first-time load or filter
  const [loadingMore, setLoadingMore] = useState(false); // for load more only

  const [answersMap, setAnswersMap] = useState({});

  const handleOpenFilter = () => setShowFilter(true);
  const handleCloseFilter = () => setShowFilter(false);

  const observer = useRef();
  const lastQuestionRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          handleLoadMore();
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingMore, hasMore]
  );

  const loggedInUser = "S3Fwc3A1R2tqeHBPNThNWWp5cy9hdz09";
  const token = "kasejfksjdhfywterjwefbdskgfhsdfjh";

  useEffect(() => {
    (async () => {
      const picked = await initializePickedQuestions();     // 1. Get picked question IDs first
      await fetchTemplateDetails(picked);                   // 2. Populate subjectLimits using them
      await fetchQuestions(picked);                         // 3. Mark those as isPicked = true
    })();
  }, []);


  const fetchQuestions = async (picked, filters = {}, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", "getQuestionDetails");
      fd.append("paperId", id);
      fd.append("page", filters.page || page);
      fd.append("limit", filters.limit || itemsPerPage);

      if (filters.subjectId) fd.append("subject", filters.subjectId);
      if (filters.chapterId) fd.append("chapter", filters.chapterId);
      if (filters.topicId) fd.append("topic", filters.topicId);
      if (filters.examType) fd.append("examType", filters.examType);
      if (filters.level) fd.append("level", filters.level);
      if (filters.questionType) fd.append("questionType", filters.questionType);
      if (filters.search) fd.append("search", filters.search);

      const response = await axios.post(
        "https://qp.sarvajna.co.in/api/controller/question/questionCurl",
        fd
      );

      console.log(response)

      let qList = response.data?.questions || [];
      const pickedSet = new Set(picked.map((q) => String(q.questionId)));

      qList = qList.map((q) => ({
        ...q,
        isPicked: pickedSet.has(q.id),
      }));

      setHasMore(qList.length === (filters.limit || itemsPerPage));
      setQuestions(prev => append ? [...prev, ...qList] : qList);
      setFetchedData(prev => append ? [...prev, ...qList] : qList);
    } catch (err) {
      console.error("Error fetching questions:", err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  async function fetchAnswers(questionId) {
    try {
      const programType = "getOptionsAndAnsForQuestion";
      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("questionId", questionId);
      fd.append("programType", programType);

      const response = await axios.post(
        "https://qp.sarvajna.co.in/api/controller/question/questionCurl",
        fd
      );

      const options = response.data?.options || [];

      setAnswersMap(prev => ({
        ...prev,
        [questionId]: options // ✅ store only options
      }));
    } catch (error) {
      console.error("Error fetching options:", error);
    }
  }




  const applyFilters = async () => {
    setPage(1);
    const picked = await initializePickedQuestions();
    const filters = {
      subjectId: selectedSubject,
      chapterId: selectedChapter,
      topicId: selectedTopic,
      examType: examTypeFilter,
      level: selectedLevel,
      questionType: selectedQuestionType,
      search: searchQuery, // ✅ already included
      page: 1,
      limit: itemsPerPage,
    };

    await fetchQuestions(picked, filters, false);
    setShowFilter(false);
  };


  const handleLoadMore = async () => {
    const nextPage = page + 1;
    setPage(nextPage);
    const picked = await initializePickedQuestions();
    const filters = {
      subjectId: selectedSubject,
      chapterId: selectedChapter,
      topicId: selectedTopic,
      examType: examTypeFilter,
      level: selectedLevel,
      questionType: selectedQuestionType,
      search: searchQuery,
      page: nextPage,
      limit: itemsPerPage,
    };
    await fetchQuestions(picked, filters, true);
  };





  const renderLatexText = (text) => {
    if (!text) return null; // Check for undefined, null, or empty string

    const decodedText = he.decode(text); // Decode HTML entities
    const latexAndImageRegex =
      /\\\((.*?)\\\)|\\\[(.*?)\\\]|<img[^>]*\bsrc=(["'])(.*?)\3[^>]*>|\\begin{align}([\s\S]*?)\\end{align}/g;

    const parts = [];
    let lastIndex = 0;

    decodedText.replace(
      latexAndImageRegex,
      (
        match,
        inlineLatex,
        blockLatex,
        quote,
        imageSrc,
        alignContent,
        index
      ) => {
        if (index > lastIndex) {
          parts.push(decodedText.slice(lastIndex, index));
        }

        if (imageSrc) {
          parts.push(
            <img
              key={index}
              src={imageSrc}
              alt="Image"
              style={{
                maxWidth: "100%",
                display: "inline",
                verticalAlign: "middle",
                marginLeft: "4px",
                marginRight: "4px",
              }}
            />
          );
        } else if (alignContent) {
          parts.push(
            <BlockMath
              key={index}
              style={{
                display: "block",
                textAlign: "center",
                margin: "10px 0",
                whiteSpace: "pre-line", // Preserves line breaks
              }}
            >
              {`\\begin{align}${alignContent}\\end{align}`}
            </BlockMath>
          );
        } else if (inlineLatex || blockLatex) {
          parts.push(
            <InlineMath
              key={index}
              style={{
                display: "inline",
                verticalAlign: "middle",
                marginLeft: "4px",
                marginRight: "4px",
              }}
            >
              {inlineLatex || blockLatex}
            </InlineMath>
          );
        }

        lastIndex = index + match.length;
      }
    );

    if (lastIndex < decodedText.length) {
      parts.push(decodedText.slice(lastIndex));
    }

    return (
      <span style={{ whiteSpace: "normal", display: "inline" }}>{parts}</span>
    );
  };

  async function fetchTemplateDetails() {

    const programType = "getTemplateDetailsInAddQuestions";

    const fd = new FormData();
    fd.append("programType", programType);
    fd.append("loggedInUser", loggedInUser);
    fd.append("token", token);
    fd.append("id", id);




    try {
      const response = await axios.post("https://qp.sarvajna.co.in/api/controller/paper/paperCurl", fd);

      console.log("Template Details:", response);
      const templateDetails = JSON.parse(response.data.data[0].details);


      if (templateDetails?.sections) {
        const sectionsArray = Object.values(templateDetails.sections);
        setSections(sectionsArray);

        // Build subjectMap
        const tempMap = {};
        sectionsArray.forEach((section) => {
          const subjects = section.subjects;
          if (subjects && typeof subjects === "object") {
            Object.values(subjects).forEach((subject) => {
              tempMap[subject.subjectId] = subject.subjectName;
            });
          }
        });

        setSubjectMap(tempMap); // ✅ store it in state
      }
    } catch (error) {
      console.error("Error fetching template details:", error);
    }
  }

const initializePickedQuestions = async () => {
  try {
    const fd = new FormData();
    fd.append("programType", "getSelectedQUestionInPaper");
    fd.append("loggedInUser", loggedInUser);
    fd.append("token", token);
    fd.append("paperId", id);

    const response = await axios.post(
      "https://qp.sarvajna.co.in/api/controller/picQuestion/picQuestionCurl",
      fd
    );

    console.log("Picked Questions API Raw:", response);

    // ✅ Extract array from API
    const pickedArray = Array.isArray(response.data) ? response.data : [];

    // Save for later use
    setPickedQuestions(pickedArray);

    // Mark already picked in UI if questions already loaded
    const pickedIds = pickedArray.map((item) => String(item.questionId));
    setQuestions((prevQuestions) =>
      prevQuestions.map((q) => ({
        ...q,
        isPicked: pickedIds.includes(String(q.id)),
      }))
    );

    return pickedArray;
  } catch (error) {
    console.error("Error initializing picked questions:", error);
    return [];
  }
};




  const togglePickQuestion = async (index, questionId, isCurrentlyPicked) => {
    if (!selectedSection) {
      alert("Please select a section before picking a question.");
      return;
    }

    const updated = [...questions];

    try {
      const programType = isCurrentlyPicked ? "removeQuestion" : "addQuestion";
      const fd = new FormData();
      fd.append("programType", programType);
      fd.append("loggedInUser", loggedInUser);
   fd.append("token", token);
      fd.append("paperId", id);
      fd.append("questionId", questionId);
      fd.append("section", selectedSection);



      const response = await axios.post("https://qp.sarvajna.co.in/api/controller/picQuestion/picQuestionCurl", fd);
      console.log("pick", response)
      const res = response.data;

      if (res.status === false) {
        alert(
          res.data || "Something went wrong"
        );
      } else {
        updated[index].isPicked = !isCurrentlyPicked;
        setQuestions(updated);
        alert(res.data || "Updated successfully");

        // ✅ update pickedQuestions state
        setPickedQuestions((prev) => {
          if (isCurrentlyPicked) {
            // Remove the question from pickedQuestions
            return prev.filter((q) => q.questionId !== questionId);
          } else {
            // Add the question to pickedQuestions
            return [
              ...prev,
              {
                questionId: questionId,
                sectionId: selectedSection,
                subjectId: updated[index].subjectId, // assuming this field exists
              },
            ];
          }
        });
      }
    } catch (error) {
      console.error("Error toggling question pick:", error);
      alert("Something went wrong while toggling pick");
    }
  };

  useEffect(() => {
    fetchSubjects();
  });

  async function fetchSubjects() {
    try {

      const role = localStorage.getItem("role");

      const storedSubjectIds =
        JSON.parse(localStorage.getItem("selectedSubjects")) || [];

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", "getSubjectOnChange");
      fd.append("role", role);

      const subjectResponse = await axios.post("https://qp.sarvajna.co.in/api/controller/onchange/onchangeCurl", fd);

      if (Array.isArray(subjectResponse.data)) {
        // ✅ Filter only the subjects thaft are in selectedSubjects
        const filteredSubjects = subjectResponse.data.filter((subject) =>
          storedSubjectIds.includes(String(subject.id))
        );
        setSubjects(filteredSubjects);
      } else {
        setSubjects([]);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setSubjects([]);
    }
  }

  async function fetchChapters(subjectId) {
    if (!subjectId) {
      setChapters([]);
      return;
    }

    try {

      const programType = "getChapterOnChangeMultiple";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("subjectId[]", subjectId);

      const chapterResponse = await axios.post("https://qp.sarvajna.co.in/api/controller/onchange/onchangeCurl", fd);

      if (Array.isArray(chapterResponse.data.data)) {
        // ✅ Get selected chapters from local storage
        const storedChapterIds =
          JSON.parse(localStorage.getItem("selectedChapters")) || [];

        // ✅ Filter only those chapters whose ID is in storedChapterIds
        const filteredChapters = chapterResponse.data.data.filter((chapter) =>
          storedChapterIds.includes(String(chapter.id))
        );

        setChapters(filteredChapters);
      } else {
        setChapters([]);
      }
    } catch (error) {
      console.error("Error fetching chapters:", error);
      setChapters([]);
    }
  }

  async function fetchTopics(chapterId) {
    try {

      const programType = "getTopicOnChangeMultiple";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      fd.append("paperId", id);
      fd.append("chapterId[]", chapterId);

      // console.log("Fetching topics for Chapter ID:", chapterId);

      const response = await axios.post("https://qp.sarvajna.co.in/api/controller/onchange/onchangeCurl", fd);

      // console.log("API Response:", response);

      if (Array.isArray(response.data)) {
        // console.log("Topics Fetched:", response.data); // Log topics
        setTopics(response.data);
      } else {
        console.warn("Unexpected API response format", response);
        setTopics([]);
      }
    } catch (error) {
      console.error("Error fetching topics:", error);
      setTopics([]);
    }
  }

  useEffect(() => {
    if (selectedChapter) {
      fetchTopics(selectedChapter);
    } else {
      setSelectedTopic("");
    }
  }, [selectedChapter]);

  async function fetchExamType() {
    try {

      const programType = "getExamTypeDetails";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);
      // fd.append("examType", examType);

      const response = await axios.post("https://qp.sarvajna.co.in/api/controller/examType/examTypeCurl", fd);
      // console.log("Response data:", response.data);

      if (Array.isArray(response.data.getExamTypeDetails)) {
        setExamType(response.data.getExamTypeDetails);
      } else {
        // console.error("Data is not an array:", response.data);
        setExamType([]);
      }
    } catch (error) {
      // console.error("Error fetching data:", error);
      setExamType([]);
    }
  }

  useEffect(() => {
    fetchLevel();
    fetchQuestionType();
  }, []);

  async function fetchLevel() {
    try {

      const programType = "getLevelDetails";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);

      const response = await axios.post("https://qp.sarvajna.co.in/api/controller/level/levelCurl", fd);
      // console.log("Response data:", response.data);

      if (Array.isArray(response.data.getLevelDetails)) {
        setLevels(response.data.getLevelDetails);
      } else {
        // console.error("Data is not an array:", response.data);
        setLevels([]);
      }
    } catch (error) {
      // console.error("Error fetching data:", error);
      setLevels([]);
    }
  }

  async function fetchQuestionType() {
    try {

      const programType = "getQuestionTypeDetails";

      const fd = new FormData();
      fd.append("loggedInUser", loggedInUser);
      fd.append("token", token);
      fd.append("programType", programType);

      const response = await axios.post("https://qp.sarvajna.co.in/api/controller/questionType/questionTypeCurl", fd);
      // console.log("Response data:", response.data);

      if (Array.isArray(response.data.getQuestionTypeDetails)) {
        setQuestionType(response.data.getQuestionTypeDetails);
      } else {
        // console.error("Data is not an array:", response.data);
        setQuestionType([]);
      }
    } catch (error) {
      // console.error("Error fetching data:", error);
      setQuestionType([]);
    }
  }

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchQuery.trim() === "") {
        applyFilters(); // Refetch without search
      }
    }, 300); // debounce for better UX

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);


  return (
    <div className="container" style={{ paddingBottom: "7rem", marginTop: "70px" }}>

      {/* Header with back button */}
      <div
        className="d-flex align-items-center px-3 bg-white border-bottom p-2 shadow-sm"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: "60px",
        }}
      >
        <button
          className="btn btn-link text-primary p-2 me-2"
          onClick={() => navigate(-1)}
          style={{ fontSize: "20px" }}
        >
          <i className="bi bi-arrow-left"></i>
        </button>
        <h6 className="m-0">📋 Pick or Remove Questions</h6>
      </div>

      <div className="input-group my-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Question ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              applyFilters();
            }
          }}
        />
        <button className="btn btn-primary" onClick={applyFilters}>
          Search
        </button>
      </div>

      {loading && (
        <p className="text-center text-primary">Loading...</p>
      )}




      <div className="list-group mt-3">
        {questions.map((q, index) => {
          const isLast = index === questions.length - 1;
          return (
            <div
              ref={isLast ? lastQuestionRef : null}
              key={q.id}
              className={`list-group-item position-relative shadow-sm mb-3 rounded border ${q.isPicked ? "border-success" : ""}`}
              style={{
                backgroundColor: q.isPicked ? "#e7f9f1" : "#fff",
                paddingBottom: "2rem",
                paddingRight: "100px", // ✅ Prevent text from going behind the Pick/Remove button
              }}
              onClick={() => fetchAnswers(q.id)} // ✅ call on click
            >

              <div className="d-flex align-items-start mb-2" >
                <strong className="me-2">{index + 1}.</strong>
                <strong className="flex-grow-1" >{renderLatexText(q.question)}</strong>
              </div>



              <button
                onClick={() =>
                  togglePickQuestion(
                    index,
                    q.id,
                    q.isPicked
                  )
                }
                className={`btn btn-sm position-absolute top-0 end-0 m-2 ${q.isPicked ? "btn-success" : "btn-outline-secondary"
                  }`}
                style={{
                  pointerEvents: 'auto',
                  transition: "none",
                  backgroundColor: q.isPicked ? "#198754" : "#fff",
                  color: q.isPicked ? "#fff" : "#6c757d",
                  borderColor: q.isPicked ? "#198754" : "#6c757d",
                }}
              >
                {q.isPicked ? "Remove" : "Pick"}
              </button>

              <ul className="mb-2 ps-3">
                {answersMap[q.id] && (
                  <div className="mt-3 options-grid">
                    {answersMap[q.id] && (
                      <div className="mt-3 options-grid">
                        {answersMap[q.id].map((opt, idx) => {
                          // Check length of plain text (no HTML/LaTeX tags)
                          const plainText = he.decode(opt).replace(/<[^>]+>/g, "").trim();
                          const isLong = plainText.length > 20; // threshold for long option

                          return (
                            <div
                              key={idx}
                              className={`option-item ${isLong ? "full-width" : ""}`}
                            >
                              <span className="option-number">{idx + 1}.</span>{" "}
                              <span className="option-text">{renderLatexText(opt)}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              </ul>


              {/* Bottom-right Question ID */}
              <div
                style={{
                  padding: "2px 10px 2px 10px",
                  position: "absolute",
                  bottom: "8px",
                  right: "12px",
                  borderRadius: "25px",
                  fontSize: "0.75rem",
                  color: "#fff",
                  backgroundColor: "#FFB38A"
                }}
              >
                ID: {q.id}
              </div>
            </div>

          );
        })}

        {loadingMore && (
          <div className="text-center mt-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading more...</span>
            </div>
          </div>
        )}
      </div>




      {/* Floating Menu Button */}
      <button
        onClick={handleOpenFilter}
        className="btn  rounded-circle shadow"
        style={{
          position: "fixed",
          bottom: "100px",
          right: "20px",
          width: "45px",
          height: "45px",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "26px",
          backgroundColor: "#FFFFFF"
        }}
      >
        <FaFilter style={{ fontSize: "30px" }} />
      </button>
      {/* Filter Modal */}
      <Modal
        show={showFilter}
        onHide={handleCloseFilter}
        animation={true}
        centered
        size="xs"
        dialogClassName="modern-filter-modal"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold fs-4 ">🔍 Filter Questions</Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-3 px-4">

          <div
            className="card p-10 mt-22 mb-4"
            style={{
              borderRadius: "10px",
            }}
          >
            <h5 className="mb-3" style={{ color: "#2c3e50" }}>
              Select Section
            </h5>

            {sections.map((section) => (
              <label
                key={section.sectionNumber}
                style={{
                  display: "block",
                  cursor: "pointer",
                  padding: "10px 12px",
                  marginBottom: "8px",
                  borderRadius: "6px",
                  backgroundColor:
                    selectedSection === section.sectionNumber
                      ? "#ECF2FE"
                      : "#fff",
                  border:
                    selectedSection === section.sectionNumber
                      ? "1px solid #085ED6"
                      : "1px solid #ddd",
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <input
                  type="radio"
                  name="section"
                  value={section.sectionNumber}
                  checked={selectedSection === section.sectionNumber}
                  onChange={() => setSelectedSection(section.sectionNumber)}
                  style={{ marginRight: "10px" }}
                />
                Section {section.sectionNumber}
              </label>
            ))}
          </div>
          <div className="row g-3">
            {/* Subject */}
            <div className="col-md-6">
              <label className="form-label">Subject</label>
              <select
                className="form-select"
                value={selectedSubject}
                onChange={async (e) => {
                  const subjectId = e.target.value;
                  setSelectedSubject(subjectId);
                  setSelectedChapter("");
                  setSelectedTopic("");
                  setChapters([]);
                  setTopics([]);
                  await fetchChapters(subjectId);
                }}
              >
                <option value="">Select Subject</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.subjectName}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter */}
            <div className="col-md-6">
              <label className="form-label">Chapter</label>
              <select
                className="form-select"
                value={selectedChapter}
                onChange={(e) => setSelectedChapter(e.target.value)}
              >
                <option value="">Select Chapter</option>
                {chapters.map((chapter) => (
                  <option key={chapter.id} value={chapter.id}>
                    {chapter.chapterName} ({chapter.chapterCount})
                  </option>
                ))}
              </select>
            </div>

            {/* Topic */}
            <div className="col-md-6">
              <label className="form-label">Topic</label>
              <select
                className="form-select"
                value={selectedTopic}
                onChange={(e) => setSelectedTopic(e.target.value)}
              >
                <option value="">Select Topic</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.topicName} ({topic.topicCount})
                  </option>
                ))}
              </select>
            </div>

            {/* Exam Type */}
            <div className="col-md-6">
              <label className="form-label">Exam Type</label>
              <input
                type="text"
                className="form-control"
                placeholder="Filter by Exam Type"
                value={examTypeFilter}
                onChange={(e) => setExamTypeFilter(e.target.value.trim() || null)}
              />
            </div>

            {/* Level */}
            <div className="col-md-6">
              <label className="form-label">Level</label>
              <select
                className="form-select"
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
              >
                <option value="">Select Level</option>
                {levels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.levelName}
                  </option>
                ))}
              </select>
            </div>

            {/* Question Type */}
            <div className="col-md-6">
              <label className="form-label">Question Type</label>
              <select
                className="form-select"
                value={selectedQuestionType}
                onChange={(e) => setSelectedQuestionType(e.target.value)}
              >
                <option value="">Select Question Type</option>
                {questionType.map((qt) => (
                  <option key={qt.id} value={qt.id}>
                    {qt.questionType}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div className="d-flex justify-content-between gap-3 mt-4">
            <button className="btn btn-outline-secondary w-100" onClick={async () => {
              const picked = await initializePickedQuestions();
              await fetchQuestions(picked);
              setShowFilter(false);
            }}>
              Reset
            </button>

            <button className="btn btn-secondary w-100" style={{ color: "white" }} onClick={applyFilters}>
              Apply Filters
            </button>
          </div>
        </Modal.Body>
      </Modal>


    </div>

  );
};

export default PickQuestions;
